import { useState, useMemo, useCallback } from "react";
import { format, parseISO } from "date-fns";
import PageHeader from "../components/PageHeader";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableRow,
    CommonTableCell,
} from "../components/table";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import {
    useGetOperationLogsQuery,
    resolveActionLabel,
    type OperationLogItem,
} from "../shared/api/endpoints/operationLogsApi";

// ---------- 定数 ----------

const EMPTY_VALUE_KEY = "__empty__";
const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? "(未設定)" : key;

type SortColumn = "timestamp" | "user_id" | "page" | "action";

// ---------- ヘルパー ----------

const formatTimestamp = (iso: string): string => {
    try {
        return format(parseISO(iso), "yyyy.M.d　HH：mm");
    } catch {
        return iso;
    }
};

const getDateKey = (iso: string): string => {
    try {
        return format(parseISO(iso), "yyyy.M.d");
    } catch {
        return EMPTY_VALUE_KEY;
    }
};

const getPage = (item: OperationLogItem): string => {
    const page = item.detail?.page;
    return typeof page === "string" ? page : "";
};

// ---------- コンポーネント ----------

const OperationLogsPage = () => {
    const { data, isLoading, isError } = useGetOperationLogsQuery({});

    const logs: OperationLogItem[] = useMemo(
        () => data?.logs ?? [],
        [data],
    );

    // ---------- フィルター（列ごと） ----------

    const timestampFilter = useColumnFilter(
        logs,
        (item) => getDateKey(item.timestamp),
        labelOrEmpty,
    );
    const userIdFilter = useColumnFilter(
        logs,
        (item) => item.user_id || EMPTY_VALUE_KEY,
        labelOrEmpty,
    );
    const pageFilter = useColumnFilter(
        logs,
        (item) => getPage(item) || EMPTY_VALUE_KEY,
        labelOrEmpty,
    );
    const actionFilter = useColumnFilter(
        logs,
        (item) => resolveActionLabel(item.action),
        labelOrEmpty,
    );

    // ---------- ソート ----------

    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (col: SortColumn): SortDirection =>
        activeSort?.column === col ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (col: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column: col, direction: dir });
        },
        [],
    );

    // ---------- ポップアップ排他制御 ----------

    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                timestamp: timestampFilter,
                user_id: userIdFilter,
                page: pageFilter,
                action: actionFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [timestampFilter, userIdFilter, pageFilter, actionFilter],
    );

    // ---------- 表示データ（フィルター + ソート適用） ----------

    const displayedLogs = useMemo(() => {
        let list = logs;

        if (timestampFilter.isFiltering) {
            const set = new Set(timestampFilter.selected);
            list = list.filter((item) => set.has(getDateKey(item.timestamp)));
        }
        if (userIdFilter.isFiltering) {
            const set = new Set(userIdFilter.selected);
            list = list.filter((item) => set.has(item.user_id || EMPTY_VALUE_KEY));
        }
        if (pageFilter.isFiltering) {
            const set = new Set(pageFilter.selected);
            list = list.filter((item) => set.has(getPage(item) || EMPTY_VALUE_KEY));
        }
        if (actionFilter.isFiltering) {
            const set = new Set(actionFilter.selected);
            list = list.filter((item) => set.has(resolveActionLabel(item.action)));
        }

        if (activeSort) {
            const cmp = (a: OperationLogItem, b: OperationLogItem): number => {
                switch (activeSort.column) {
                    case "timestamp":
                        return a.timestamp.localeCompare(b.timestamp);
                    case "user_id":
                        return a.user_id.localeCompare(b.user_id, "ja");
                    case "page":
                        return getPage(a).localeCompare(getPage(b), "ja");
                    case "action":
                        return resolveActionLabel(a.action).localeCompare(
                            resolveActionLabel(b.action),
                            "ja",
                        );
                }
            };
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        logs,
        timestampFilter.isFiltering, timestampFilter.selected,
        userIdFilter.isFiltering, userIdFilter.selected,
        pageFilter.isFiltering, pageFilter.selected,
        actionFilter.isFiltering, actionFilter.selected,
        activeSort,
    ]);

    // ---------- JSX ----------

    return (
        <>
            <PageHeader title="操作ログ一覧" />
            <div className="px-8 pb-6">
                    {isLoading && (
                        <div className="flex items-center justify-center h-32 text-sm text-gray-400">
                            読み込み中...
                        </div>
                    )}
                    {isError && (
                        <div className="flex items-center justify-center h-32 text-sm text-red-500">
                            ログの取得に失敗しました。
                        </div>
                    )}
                    {!isLoading && !isError && (
                        <div className="w-3/4 mx-auto min-w-[680px]">
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="rounded-lg overflow-hidden border border-gray-200">
                        <div className="max-h-[calc(100svh-200px)] overflow-y-auto">
                        <CommonTable fullWidth>
                            <thead className="sticky top-0 z-20">
                                <tr>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={timestampFilter.open}
                                        sortDirection={sortFor("timestamp")}
                                        filterCount={timestampFilter.filterCount}
                                        onSort={(anchor) => openOnly("timestamp", anchor)}
                                        className="min-w-[160px]"
                                    >
                                        日時
                                    </CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={userIdFilter.open}
                                        sortDirection={sortFor("user_id")}
                                        filterCount={userIdFilter.filterCount}
                                        onSort={(anchor) => openOnly("user_id", anchor)}
                                        className="min-w-[160px]"
                                    >
                                        ユーザーID
                                    </CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={pageFilter.open}
                                        sortDirection={sortFor("page")}
                                        filterCount={pageFilter.filterCount}
                                        onSort={(anchor) => openOnly("page", anchor)}
                                        className="min-w-[160px]"
                                    >
                                        操作画面
                                    </CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={actionFilter.open}
                                        sortDirection={sortFor("action")}
                                        filterCount={actionFilter.filterCount}
                                        onSort={(anchor) => openOnly("action", anchor)}
                                        className="min-w-[160px]"
                                    >
                                        操作内容
                                    </CommonTableHeaderCell>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="h-20 text-center text-sm text-gray-400">
                                            ログがありません。
                                        </td>
                                    </tr>
                                ) : (
                                    displayedLogs.map((item) => (
                                        <CommonTableRow key={item.log_id}>
                                            <CommonTableCell>{formatTimestamp(item.timestamp)}</CommonTableCell>
                                            <CommonTableCell>{item.user_id}</CommonTableCell>
                                            <CommonTableCell>{getPage(item) || "—"}</CommonTableCell>
                                            <CommonTableCell>{resolveActionLabel(item.action)}</CommonTableCell>
                                        </CommonTableRow>
                                    ))
                                )}
                            </tbody>
                        </CommonTable>
                        </div>
                        </div>
                        </div>
                        </div>
                    )}
            </div>

            {/* 列ごとの並び替え＋絞り込みポップアップ */}
            <ColumnFilterPopup
                isOpen={timestampFilter.open}
                onClose={() => timestampFilter.setOpen(false)}
                anchorRef={timestampFilter.anchorRef}
                sortDirection={sortFor("timestamp")}
                onSortChange={handleSortChange("timestamp")}
                options={timestampFilter.options}
                selectedValues={timestampFilter.selected}
                onSelectedValuesChange={timestampFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={userIdFilter.open}
                onClose={() => userIdFilter.setOpen(false)}
                anchorRef={userIdFilter.anchorRef}
                sortDirection={sortFor("user_id")}
                onSortChange={handleSortChange("user_id")}
                options={userIdFilter.options}
                selectedValues={userIdFilter.selected}
                onSelectedValuesChange={userIdFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={pageFilter.open}
                onClose={() => pageFilter.setOpen(false)}
                anchorRef={pageFilter.anchorRef}
                sortDirection={sortFor("page")}
                onSortChange={handleSortChange("page")}
                options={pageFilter.options}
                selectedValues={pageFilter.selected}
                onSelectedValuesChange={pageFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={actionFilter.open}
                onClose={() => actionFilter.setOpen(false)}
                anchorRef={actionFilter.anchorRef}
                sortDirection={sortFor("action")}
                onSortChange={handleSortChange("action")}
                options={actionFilter.options}
                selectedValues={actionFilter.selected}
                onSelectedValuesChange={actionFilter.setSelected}
            />
        </>
    );
};

export default OperationLogsPage;
